// Agent-callable tool that dry-runs a kernel definition and reports
// back whether it executed cleanly. Used by the kernel-save repair
// loop: the sub-agent proposes a kernel JSON, calls test_kernel,
// reads the error (if any), and iterates until the recipe runs
// end-to-end.
//
// Returns one of two shapes via `details`:
//   { ok: true,  summary }          — recipe executed cleanly
//   { ok: false, stage, error, ... } — recipe failed at `stage`
//                                       (analytics_query / python /
//                                        render-shape), with the
//                                       runner's error message.
//
// The tool intentionally returns SUCCESS (isError: false) on both
// shapes — a failed dry-run is information for the agent, not a tool
// error. Real tool errors (the test itself crashing) still surface
// as isError to keep failure visibility consistent.

import { Type } from "@earendil-works/pi-ai";
import { runKernel } from "./kernelRunner.js";
import { KNOWN_RENDER_KINDS } from "./kernelStore.js";
import { coerceJsonArg } from "./coerceJsonArg.js";
import { kernelErrorMessageForCurrentUser } from "./kernelErrorCopy.js";
import { formatRenderIssues, inspectKernelRender } from "./kernelRenderValidation.js";
import {
    asRunContext,
    resolvedKernelFromModel,
} from "./kernelResolver.js";

export function createTestKernelTool({ runContext = null } = {}) {
    const modelRunContext = asRunContext(runContext);
    return {
    name: "test_kernel",
    label: "Test kernel",
    description:
        "Dry-run a candidate kernel definition end-to-end. Returns { ok: true, summary } on success or { ok: false, stage, error } when a step fails. Use this to iterate on kernel JSON until it executes cleanly:\n" +
        "  1. Build a kernel JSON.\n" +
        "  2. Call test_kernel with it.\n" +
        "  3. If ok=false, read the error + stage, fix the offending step, retry.\n" +
        "  4. When ok=true, call return_result with the same kernel JSON.\n\n" +
        "Common stages of failure:\n" +
        "  • analytics_query — wrong field/entity name (use schema_search / schema_describe_entity to look up the right name) or malformed spec shape.\n" +
        "  • python         — runtime error inside the Python step. The error message includes the traceback. Reminder: the runner exposes `inputs` and `params` as separate top-level globals (real Python dicts; there is no `data` envelope around them). Use `inputs[\"q1\"]` and `params[\"days\"]` directly. References to prior chat state (variables that don't exist in the kernel's fresh namespace) need to be wired through the step's `inputs` field.\n" +
        "  • render         — render spec couldn't find the data it expected. Usually means the last step's output doesn't match the render's `valueField` / column names, or `renderSource` points at the wrong step.",
    // `kernel` / `params` are typed as objects (not Type.Unknown) so the
    // model's tool-call decoder is steered to emit an object literal rather
    // than a JSON-encoded string — the cross-model double-encoding that
    // broke kernel-save when Claude sent `kernel: "{...}"`. Mirrors
    // render_block's typed `spec`. Shape is loose (additionalProperties,
    // nothing required) so the tool body below keeps owning validation and
    // returns its informative per-stage messages. coerceJsonArg in execute()
    // is the safety net for any model that stringifies anyway.
    parameters: Type.Object({
        kernel: Type.Object({
            command: Type.Optional(Type.String()),
            title: Type.Optional(Type.String()),
            steps: Type.Optional(Type.Array(Type.Unknown())),
            render: Type.Optional(Type.Unknown()),
        }, {
            additionalProperties: true,
            description:
                "Complete kernel definition to test, as an INLINE JSON OBJECT — not a JSON-encoded string. Shape: { command, title, steps: [{kind, id, spec?, code?, inputs?}], render: {kind, ...}, renderSource?, params? }. The runner walks steps in order; renderSource picks which step's output feeds the render's data (defaults to the last step).",
        }),
        params: Type.Optional(
            Type.Object({}, {
                additionalProperties: true,
                description: "Parameter overrides to use for this test run, as an inline JSON object (not a string). Usually omit — defaults from the kernel are used.",
            }),
        ),
    }),
    async execute(_id, args) {
        // Some model families double-encode object args as a JSON string.
        // Decode before use so a stringified kernel/params doesn't trip the
        // object check below (mirrors render_block/canvas_*).
        const kernel = coerceJsonArg(args?.kernel);
        if (!kernel || typeof kernel !== "object") {
            return failTool("test_kernel needs a kernel object");
        }
        if (!Array.isArray(kernel.steps) || !kernel.steps.length) {
            return failTool("kernel needs at least one step");
        }
        if (!kernel.render || typeof kernel.render !== "object") {
            return failTool("kernel needs a render spec");
        }
        if (!KNOWN_RENDER_KINDS.has(kernel.render.kind)) {
            return failTool(
                `kernel render is missing a valid \`kind\` (got ${JSON.stringify(kernel.render.kind)}). ` +
                `Must be one of: ${[...KNOWN_RENDER_KINDS].join(", ")}.`,
            );
        }

        let result;
        try {
            result = await runKernel({
                resolvedDefinition: resolvedKernelFromModel(
                    kernel,
                    modelRunContext,
                ),
                runContext: modelRunContext,
                params: coerceJsonArg(args?.params) || {},
            });
        } catch (err) {
            const message = await kernelErrorMessageForCurrentUser(err, "kernel test failed");
            return failTool(`test_kernel crashed: ${message}`);
        }

        if (result.ok) {
            const renderValidation = inspectKernelRender(
                result.renderSpec || kernel.render,
                result.renderData,
            );
            if (renderValidation.status === "invalid_render") {
                const payload = {
                    ok: false,
                    stage: "render",
                    renderStatus: renderValidation.status,
                    issues: renderValidation.issues,
                    renderData: previewValue(result.renderData),
                    error: formatRenderIssues(renderValidation.issues),
                    summary: `kernel failed at render: ${formatRenderIssues(renderValidation.issues)}`,
                };
                return {
                    content: [{ type: "text", text: JSON.stringify(payload) }],
                    details: payload,
                };
            }
            const payload = describeSuccess(kernel, result);
            payload.renderStatus = renderValidation.status;
            return {
                content: [{ type: "text", text: JSON.stringify(payload) }],
                details: payload,
            };
        }

        const payload = await describeFailure(kernel, result);
        return {
            content: [{ type: "text", text: JSON.stringify(payload) }],
            details: payload,
        };
    },
    };
}

// Compatibility export for callers that have not established a model context.
// It is intentionally UNKNOWN and therefore cannot mint capability.
export const testKernelTool = createTestKernelTool();

function describeSuccess(kernel, result) {
    return {
        ok: true,
        stepCount: kernel.steps.length,
        renderKind: kernel.render?.kind,
        renderDataShape: shapeOf(result.renderData),
        params: result.params,
        summary: `kernel ran cleanly · ${kernel.steps.length} steps · render=${kernel.render?.kind} · data=${shapeOf(result.renderData)}`,
    };
}

async function describeFailure(kernel, result) {
    const failedIdx = typeof result.failedStepIndex === "number" ? result.failedStepIndex : null;
    const step = failedIdx != null ? kernel.steps[failedIdx] : null;
    const stage = step?.kind === "analytics_query"
        ? "analytics_query"
        : step?.kind === "python"
            ? "python"
            : "render";
    const error = await kernelErrorMessageForCurrentUser(result.error, "unknown error");
    return {
        ok: false,
        stage,
        failedStepIndex: failedIdx,
        failedStepId: step?.id ?? null,
        failedStepKind: step?.kind ?? null,
        error,
        summary: `kernel failed at step ${failedIdx} (${step?.id || "?"} · ${step?.kind || "?"}): ${error}`,
    };
}

function shapeOf(value) {
    if (value == null) return "null";
    if (Array.isArray(value)) {
        if (!value.length) return "empty array";
        const first = value[0];
        if (typeof first === "number") return `array<number> · ${value.length} items`;
        if (first && typeof first === "object") {
            const cols = Object.keys(first).slice(0, 6).join(", ");
            return `array<row> · ${value.length} rows · cols=[${cols}]`;
        }
        return `array · ${value.length} items`;
    }
    if (typeof value === "object") {
        const cols = Object.keys(value).slice(0, 6).join(", ");
        return `object · keys=[${cols}]`;
    }
    return typeof value;
}

function previewValue(value, maxRows = 10) {
    if (value == null) return value;
    if (Array.isArray(value)) {
        return {
            _type: "array",
            length: value.length,
            sample: value.slice(0, maxRows),
        };
    }
    if (typeof value === "object" && Array.isArray(value.rows)) {
        return {
            _type: "table",
            columns: value.columns || null,
            rowCount: value.rows.length,
            sampleRows: value.rows.slice(0, maxRows),
        };
    }
    return value;
}

function failTool(message) {
    return {
        content: [{ type: "text", text: message }],
        details: { error: message },
        isError: true,
    };
}
